/* eslint-disable @typescript-eslint/strict-boolean-expressions */
import CheckCircleIcon from "@atlaskit/icon/core/check-circle";
import CrossIcon from "@atlaskit/icon/core/cross";
import {token} from "@atlaskit/tokens";
import {useEffect} from "react";

export function TableOperationToasts({
    showCopyToast,
    onDismissCopyToast,
    showPendingDeleteToast,
    onDismissPendingDeleteToast,
    onUndoDelete,
}: {
    showCopyToast: boolean
    onDismissCopyToast: () => void
    showPendingDeleteToast: boolean
    onDismissPendingDeleteToast: () => void
    onUndoDelete: () => void
}) {
    return (
        <div
            style={{
                position: "fixed",
                bottom: token("space.200"),
                left: token("space.200"),
                zIndex: 9999,
                display: "grid",
                gap: token("space.100"),
            }}
        >
            {showCopyToast ? (
                <TableSuccessToastCard
                    title="Rows has been copied to clipboard"
                    onClose={onDismissCopyToast}
                />
            ) : null}
            {showPendingDeleteToast ? (
                <TableSuccessToastCard
                    title="Selected items have been deleted"
                    description="Rows deleted. You can undo to get them back"
                    onClose={onDismissPendingDeleteToast}
                    actionLabel="Undo"
                    onAction={onUndoDelete}
                />
            ) : null}
        </div>
    );
}

/** Restore success toast surface (design spec). */
export const RESTORE_SUCCESS_TOAST_BACKGROUND = "#EFFFD6";

/** Fixed-position success toast (e.g. after restore). */
export function TableSuccessToast({
    title,
    description,
    onClose,
    autoDismissSeconds = 4,
    backgroundColor,
}: {
    title: string
    description?: string
    onClose: () => void
    autoDismissSeconds?: number
    backgroundColor?: string
}) {
    useEffect(() => {
        if (autoDismissSeconds === undefined || autoDismissSeconds <= 0) {
            return;
        }
        const timeoutId = setTimeout(onClose, autoDismissSeconds * 1000);
        return () => clearTimeout(timeoutId);
    }, [autoDismissSeconds, onClose]);

    return (
        <div
            style={{
                position: "fixed",
                bottom: token("space.200"),
                left: token("space.200"),
                zIndex: 9999,
            }}
        >
            <TableSuccessToastCard
                title={title}
                description={description}
                onClose={onClose}
                backgroundColor={backgroundColor}
            />
        </div>
    );
}

function TableSuccessToastCard({
    title,
    description,
    onClose,
    actionLabel,
    onAction,
    backgroundColor,
}: {
    title: string
    description?: string
    onClose: () => void
    actionLabel?: string
    onAction?: () => void
    backgroundColor?: string
}) {
    return (
        <div
            style={{
                width: "368px",
                padding: token("space.200"),
                display: "grid",
                gap: token("space.200"),
                borderRadius: "3px",
                background: backgroundColor ?? token("color.background.success"),
            }}
        >
            <div
                style={{
                    display: "grid",
                    gridTemplateColumns: "auto 1fr auto",
                    alignItems: "start",
                    gap: token("space.200"),
                    color: token("color.text"),
                    fontFamily: "Inter",
                    fontSize: "14px",
                    fontStyle: "normal",
                    fontWeight: 653,
                    lineHeight: "20px",
                }}
            >
                <CheckCircleIcon label="" color={token("color.icon.success")} />
                <span>{title}</span>
                <button
                    type="button"
                    onClick={onClose}
                    style={{background: "transparent", border: "none", cursor: "pointer", color: token("color.icon.subtle")}}
                >
                    <CrossIcon label="" />
                </button>
            </div>
            {description ? (
                <div
                    style={{
                        color: token("color.text"),
                        fontFeatureSettings: "'liga' off, 'calt' off",
                        fontFamily: "Inter",
                        fontSize: "14px",
                        fontStyle: "normal",
                        fontWeight: 400,
                        lineHeight: "20px",
                    }}
                >
                    {description}
                </div>
            ) : null}
            {actionLabel && onAction ? (
                <button
                    type="button"
                    onClick={onAction}
                    style={{
                        color: token("color.link"),
                        textAlign: "left",
                        fontFeatureSettings: "'liga' off, 'calt' off",
                        fontFamily: "Inter",
                        fontSize: "14px",
                        fontStyle: "normal",
                        fontWeight: 400,
                        lineHeight: "20px",
                        background: "transparent",
                        border: "none",
                        padding: 0,
                        cursor: "pointer",
                    }}
                >
                    {actionLabel}
                </button>
            ) : null}
        </div>
    );
}
